import api from "@/api";
import converters from "@/converters";
import {
  type MeetingAgendaPointTemplate,
  type MeetingAgendaPointTemplateInput,
} from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { ICachedData } from "@/interfaces";
import { computed, ref } from "vue";

const DEFAULT_DATA: ICachedData<MeetingAgendaPointTemplate[]> = {
  status: "none",
  data: [],
};

const dataMap = ref<ICachedData<MeetingAgendaPointTemplate[]>>({
  ...DEFAULT_DATA,
});

export function useMeetingAgendaPoints(isInit: boolean = false) {
  const init = async () => {
    const item = dataMap.value;

    if (item.status === "loaded" || isInit) {
      return;
    }

    item.status = "loading";
    const data = await api.meetingAgendaPoints.getAll();
    item.data = data || [];
    item.status = "loaded";
  };

  init();

  const agendaPoints = computed<MeetingAgendaPointTemplate[]>(
    () => dataMap.value.data,
  );

  const agendaPointsList = computed(() => {
    return dataMap.value.data
      .map(converters.meetingAgendaPoints.toMultiSelectItem)
      .toSorted((a, b) => a.title.localeCompare(b.title));
  });

  const agendaPointsOptions = computed(() => {
    return dataMap.value.data
      .map(converters.meetingAgendaPoints.toOption)
      .toSorted((a, b) => a.title.localeCompare(b.title));
  });

  const loading = computed(() => {
    return dataMap.value.status === "loading";
  });

  const status = computed(() => dataMap.value.status);

  const isReady = () => isDataReady(status);

  const refetch = async () => {
    const data = await api.meetingAgendaPoints.getAll();
    dataMap.value.data = data || [];
  };

  const save = async (input: MeetingAgendaPointTemplateInput) => {
    const response = await api.meetingAgendaPoints.save(input);
    await refetch();
    return response;
  };

  const deleteItem = async (id: number) => {
    const response = await api.meetingAgendaPoints.deleteItem(id.toString());
    await refetch();
    return response;
  };

  return {
    agendaPoints,
    agendaPointsList,
    agendaPointsOptions,
    loading,
    refetch,
    isReady,
    save,
    deleteItem,
  };
}
